/*
NOTES:
  React → needed for React components
  useState → used to store values that can change
  useEffect → used to run code when something changes, like loading data from backend
  MainGrid → layout wrapper for the page
  SidebarEmployees → left side panel
  SalaryCenter → middle section
  ProfilePanel → right side panel
*/


import React, { useEffect, useMemo, useState } from "react";
import MainGrid from "../../components/layout/MainGrid";
import API_BASE from "../../config";
import { getCurrentStoreName } from "../../utils/stores";
import { useCompanyDefaults } from "../../utils/companyDefaults";

import SidebarEmployees from "./components/SidebarEmployees";
import SalaryCenter from "./components/SalaryCenter";
import ProfilePanel from "./components/ProfilePanel";

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}

/** Identity headers — same shape as TimesheetPage so role-gated
 *  endpoints (the timesheet list in particular) accept the request. */
function authHeaders(extra) {
  const u = getUser() || {};
  return {
    "Content-Type": "application/json",
    "X-User-Role": u.role || "",
    "X-Employee-Code": u.employeeCode || "",
    "X-Username": u.username || "system",
    ...(extra || {}),
  };
}

/** Parse a leavesJson blob (string or already-parsed array) into the
 *  flat view-model used by ProfilePanel and the payslip PDF. */
function parseLeaveList(raw) {
  if (!raw) return [];
  try {
    const d = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(d)) return [];
    return d.map(o => ({
      name: String(o?.name || ""),
      days: Number(o?.days) || 0,
      convertible: !!o?.convertible,
    })).filter(l => l.name);
  } catch {
    return [];
  }
}

export default function SalaryPage() {
  const PAGE_SIZE = 5;
  // Live company defaults — changes in Settings notify this hook so
  // the profile re-renders without a reload.
  const companyDefaults = useCompanyDefaults();

  
  //To avoid refetching all employees every time page changes, you can fetch once, then paginate locally.
  const [allEmployees, setAllEmployees] = useState([]);

  // stores the current employee list shown in the sidebar
  // this is only the current page of employees, not all employees
  // starts as empty array []
  const [employees, setEmployees] = useState([]);

  // stores which employee is currently selected
  // this is the employee code, not the employee name
  // likely something like "EMP001"
  const [selectedCode, setSelectedCode] = useState(null);

  // stores the data for the right panel
  // starts as null because nothing is loaded yet
  const [profile, setProfile] = useState(null);

  // show loading immediately
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  // stores pagination info for the sidebar
  // stores current page number
  // starts at 0
  // so page numbering here is zero-based
  //    page 0 = first page
  //    page 1 = second page
  const [page, setPage] = useState(0);

  // stores total number of pages for pagination
  const [totalPages, setTotalPages] = useState(1);

  // stores current calendar month and year selection in the SalaryCenter
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth()); // JS month is 0-based
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());

  const [calendarDays, setCalendarDays] = useState([]);
  const [timesheetSummary, setTimesheetSummary] = useState({ worked: 0, overtime: 0, leave: 0 });
  // Days used this year per leave-type title, for the Leave Credits
  // panel on ProfilePanel. Mirrors what CalendarPage shows so both
  // views agree on "used / remaining".
  const [leaveUsedByName, setLeaveUsedByName] = useState({});
  // Raw timesheets for the current employee/month — needed by the
  // payroll computation card in SalaryCenter (per-day bucketing).
  const [timesheets, setTimesheets] = useState([]);

  const [debouncedSelectedCode, setDebouncedSelectedCode] = useState(null);

  // Bumped whenever a timesheet edit happens on another page
  // (TimesheetPage dispatches a "timesheet:changed" window event).
  // Included in the calendar/timesheets fetch effect's deps below so
  // the payroll card reflects the edit without a manual refresh.
  const [tsVersion, setTsVersion] = useState(0);
  useEffect(() => {
    const h = () => setTsVersion(v => v + 1);
    window.addEventListener("timesheet:changed", h);
    return () => window.removeEventListener("timesheet:changed", h);
  }, []);

  // ✅ Load sidebar employees (paginated)
  // this runs when the page number changes, to load the right page of employees
  // Every time page changes, it:
  // 1. fetches all employees from backend
  // 2. calculates total pages
  // 3. gets only the 5 employees for the current page
  // 4. updates sidebar list
  // 5. auto-selects an employee if needed
  useEffect(() => {
    // This calls your Spring Boot API. // the fetch(..) below
    // It expects something like:
    // [
    //   { "code": "E001", "name": "John" },
    //   { "code": "E002", "name": "Mary" }
    // ]
  const store = getCurrentStoreName();
  const qs    = store ? `?storeName=${encodeURIComponent(store)}` : "";
  fetch(`${API_BASE}/api/employees${qs}`)
    .then((r) => r.json()) // r -> is the HTTP resopnse, and r.json() converts it to json
    .then((data) => {
      // Ensure data is an array, if not use empty array
      const all = Array.isArray(data) ? data : [];
      setAllEmployees(all);

      // Calculate total pages based on total employees and page size
      const tp = Math.max(1, Math.ceil(all.length / PAGE_SIZE));
      setTotalPages(tp);
      
      // Only auto-select once at the very beginning
      if (all.length > 0) {
        setSelectedCode(all[0].code);
      }
    })
    .catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const start = page * PAGE_SIZE;
    const list = allEmployees.slice(start, start + PAGE_SIZE);
    setEmployees(list);
  }, [page, allEmployees]);

  // ✅ Load right panel when selection changes
  // Whenever the selected employee changes, this loads all data for that employee.
  useEffect(() => {
    if (!selectedCode) return;


    // This calls multiple APIs in parallel to get all data for the selected employee.
    // This runs all 4 requests together in parallel.
    Promise.all([
      fetch(`${API_BASE}/api/employees/${selectedCode}`).then((r) => r.json()),
      fetch(`${API_BASE}/api/employees/${selectedCode}/profile`).then((r) => r.json()),
      fetch(`${API_BASE}/api/employees/${selectedCode}/documents`).then((r) => r.json()),
      fetch(`${API_BASE}/api/employees/${selectedCode}/stats`).then((r) => r.json()),
    ])
      // emp = basic employee data
      // prof = profile object
      // docs = documents array
      // stats = stats array
      .then(([emp, prof, docs, stats]) => {
        const p = prof ?? {};
        // Parse the employee's own leave-benefits list. Company-default
        // leaves are merged in later via useMemo so Settings edits show
        // up live without needing to re-open each employee.
        const leaves = parseLeaveList(emp?.leavesJson);
        // Build final profile object
        // This combines all the backend data into one object for ProfilePanel.
        setProfile({
          ...emp,
          leaves,
          info: [
            { k: "Birthday",    v: p.birthday,    icon: "cal"   },
            { k: "Phone number",v: p.phone,        icon: "phone" },
            { k: "Email",       v: p.email,        icon: "mail"  },
            { k: "Citizenship", v: p.citizenship,  icon: "id"    },
            { k: "City",        v: p.city,         icon: "pin"   },
            { k: "Address",     v: p.address,      icon: "pin"   },
            { k: "Department",  v: emp.department, icon: "id"    },
            { k: "Status",      v: emp.status,     icon: "id"    },
            { k: "Salary",      v: emp.salary != null ? `$${Number(emp.salary).toLocaleString()}` : null, icon: "cal" },
          ].filter((x) => x.v),  // .filter(...) <-- this removes/filter out any row item if the v: is empty like v: "" or v: null or v: undefined. So only info items with a value will be shown.

          // They ensure docs and stats are always arrays.
          // so if query docs or stat with return null or something unexpected, it will default to empty array instead of breaking the UI.
          docs: Array.isArray(docs) ? docs : [],
          stats: Array.isArray(stats) ? stats : [],
        });
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Error loading profile for employee " + selectedCode, err);
        }
      });

  }, [selectedCode]);

  // Load calendar data for selected employee + merge global calendar events
  useEffect(() => {
    if (!selectedCode) return;

    const storeName = getCurrentStoreName();
    const storeQs   = storeName ? `&storeName=${encodeURIComponent(storeName)}` : "";
    const empCalUrl = `${API_BASE}/api/employees/${selectedCode}/calendar?year=${selectedYear}&month=${Number(selectedMonth) + 1}`;
    const eventsUrl = `${API_BASE}/api/calendar/events?year=${selectedYear}&month=${Number(selectedMonth) + 1}&employeeCode=${selectedCode}${storeQs}`;
    const tsUrl = `${API_BASE}/api/timesheets?employeeCode=${selectedCode}&year=${selectedYear}&month=${Number(selectedMonth) + 1}`;

    Promise.all([
      fetch(empCalUrl).then(r => r.ok ? r.json() : []),
      fetch(eventsUrl).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(tsUrl, { headers: authHeaders() }).then(r => r.ok ? r.json() : []).catch(() => []),
    ])
    .then(([empData, globalEvents, tsData]) => {
      // Compute timesheet summary
      const tsList = Array.isArray(tsData) ? tsData : [];
      setTimesheets(tsList);
      let worked = 0, overtime = 0, leaveHrs = 0;
      const tsDates = new Set();
      for (const ts of tsList) {
        worked += ts.workedHours || 0;
        overtime += ts.overtimeHours || 0;
        if (ts.status === "Leave") { leaveHrs += 8; tsDates.add(ts.date); }
      }

      // Also count leave hours from calendar Leave events that don't
      // already have a matching timesheet "Leave" entry. Only APPROVED
      // leaves (or legacy null-status rows that predate the workflow)
      // count toward salary — PENDING and REJECTED do not.
      const allGlobalEvents = Array.isArray(globalEvents) ? globalEvents : [];
      for (const ev of allGlobalEvents) {
        if ((ev.eventType || "").toLowerCase() !== "leave") continue;
        if (!ev.affectsSalary) continue;
        if (ev.status != null && ev.status !== "APPROVED") continue;
        const start = new Date(ev.startDate + "T00:00:00");
        const end = new Date((ev.endDate || ev.startDate) + "T00:00:00");
        const cur = new Date(start);
        while (cur <= end) {
          if (cur.getFullYear() === selectedYear && cur.getMonth() === Number(selectedMonth)) {
            const iso = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,"0")}-${String(cur.getDate()).padStart(2,"0")}`;
            if (!tsDates.has(iso)) leaveHrs += 8;
          }
          cur.setDate(cur.getDate() + 1);
        }
      }

      setTimesheetSummary({
        worked: Math.round(worked * 10) / 10,
        overtime: Math.round(overtime * 10) / 10,
        leave: Math.round(leaveHrs * 10) / 10,
      });
      const empDays = Array.isArray(empData) ? empData : [];

      // Merge global calendar events into employee calendar days
      const daysInMonth = new Date(selectedYear, Number(selectedMonth) + 1, 0).getDate();
      const empMap = {};
      empDays.forEach(d => { empMap[d.day] = d; });

      // Build events-by-day from global calendar events
      const eventsByDay = {};
      (Array.isArray(globalEvents) ? globalEvents : []).forEach(ev => {
        const start = new Date(ev.startDate + "T00:00:00");
        const end = new Date(ev.endDate + "T00:00:00");
        const cur = new Date(start);
        while (cur <= end) {
          if (cur.getFullYear() === selectedYear && cur.getMonth() === Number(selectedMonth)) {
            const day = cur.getDate();
            if (!eventsByDay[day]) eventsByDay[day] = [];
            eventsByDay[day].push(ev);
          }
          cur.setDate(cur.getDate() + 1);
        }
      });

      // Merge: employee calendar data + global events
      const merged = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const emp = empMap[d];
        const globalEvs = eventsByDay[d] || [];
        const eventLabels = globalEvs.map(e => e.title);

        if (emp) {
          // If employee already has event data, append global event labels
          const combinedLabel = [emp.eventLabel, ...eventLabels].filter(Boolean).join(", ");
          merged.push({
            ...emp,
            event: emp.event || globalEvs.length > 0,
            eventLabel: combinedLabel || emp.eventLabel,
            globalEvents: globalEvs,
          });
        } else if (globalEvs.length > 0) {
          // No employee-specific data but there are global events
          merged.push({
            day: d,
            striped: false,
            event: true,
            eventLabel: eventLabels.join(", "),
            globalEvents: globalEvs,
          });
        }
      }

      setCalendarDays(merged);
    })
    .catch((err) => {
      if (err.name !== "AbortError") {
        console.error("Error loading calendar data: ", err);
        setCalendarDays([]);
      }
    });
  }, [selectedCode, selectedYear, selectedMonth, tsVersion]);

  // Fetch every Leave event this employee filed in the current year,
  // aggregate by leave-type title so the profile panel can show
  // remaining / total / used just like the calendar does. Runs whenever
  // the selected employee / year / timesheet-edit cursor changes.
  useEffect(() => {
    if (!selectedCode) { setLeaveUsedByName({}); return; }
    let cancelled = false;
    const url = `${API_BASE}/api/calendar/events?year=${selectedYear}&employeeCode=${selectedCode}`;
    fetch(url)
      .then(r => r.ok ? r.json() : [])
      .then(rows => {
        if (cancelled) return;
        const arr = Array.isArray(rows) ? rows : [];
        // Build a holiday date-key set from the same year's events so
        // weekends and holidays don't draw from the leave balance.
        const holidays = new Set();
        for (const ev of arr) {
          const t = (ev.eventType || "").toLowerCase();
          if (t !== "holiday" && t !== "regular holiday" && t !== "special holiday") continue;
          const hs = new Date(ev.startDate + "T00:00:00");
          const he = new Date((ev.endDate || ev.startDate) + "T00:00:00");
          if (isNaN(hs) || isNaN(he)) continue;
          const cur = new Date(hs);
          while (cur <= he) {
            const y = cur.getFullYear();
            const m = String(cur.getMonth() + 1).padStart(2, "0");
            const d = String(cur.getDate()).padStart(2, "0");
            holidays.add(`${y}-${m}-${d}`);
            cur.setDate(cur.getDate() + 1);
          }
        }
        const used = {};
        for (const ev of arr) {
          if ((ev.eventType || "").toLowerCase() !== "leave") continue;
          if (ev.createdBy !== selectedCode) continue;
          // Only approved leaves count toward the balance. Legacy
          // rows with status = null predate the approval workflow and
          // are treated as historic approvals.
          if (ev.status != null && ev.status !== "APPROVED") continue;
          const start = new Date(ev.startDate + "T00:00:00");
          const end   = new Date((ev.endDate || ev.startDate) + "T00:00:00");
          if (isNaN(start) || isNaN(end)) continue;
          let days = 0;
          const cur = new Date(start);
          while (cur <= end) {
            if (cur.getFullYear() === selectedYear) {
              const dow = cur.getDay();
              const y = cur.getFullYear();
              const m = String(cur.getMonth() + 1).padStart(2, "0");
              const d = String(cur.getDate()).padStart(2, "0");
              const key = `${y}-${m}-${d}`;
              if (dow !== 0 && dow !== 6 && !holidays.has(key)) days++;
            }
            cur.setDate(cur.getDate() + 1);
          }
          if (days === 0) continue;
          const title = (ev.title || "Other Leave").trim();
          used[title] = (used[title] || 0) + days;
        }
        setLeaveUsedByName(used);
      })
      .catch(() => { if (!cancelled) setLeaveUsedByName({}); });
    return () => { cancelled = true; };
  }, [selectedCode, selectedYear, tsVersion]);

  // Merge company-default leaves into the profile view. Defaults the
  // employee already has (matched by name, case-insensitive) are left
  // untouched so per-employee days/convertible overrides still win.
  // Recomputes when Settings saves new defaults, so the right panel
  // and payslip reflect the change immediately.
  const profileView = useMemo(() => {
    if (!profile) return profile;
    const defaults = parseLeaveList(companyDefaults["work.defaultLeaves"]);
    const own = profile.leaves || [];
    const names = new Set(
      own.map(l => (l.name || "").trim().toLowerCase()).filter(Boolean)
    );
    const missing = defaults.filter(
      d => !names.has((d.name || "").trim().toLowerCase())
    );
    const merged = missing.length === 0 ? own : [...own, ...missing];
    // Attach this year's used-days count to each leave row so
    // ProfilePanel can render remaining / total / used.
    const withUsed = merged.map(l => {
      const usedKey = Object.keys(leaveUsedByName).find(
        k => k.trim().toLowerCase() === (l.name || "").trim().toLowerCase()
      );
      const used = usedKey ? leaveUsedByName[usedKey] : 0;
      return { ...l, used };
    });
    return { ...profile, leaves: withUsed };
  }, [profile, companyDefaults, leaveUsedByName]);

  return (
    <MainGrid>
      <SidebarEmployees
        employees={employees}
        selectedCode={selectedCode}
        onSelect={setSelectedCode}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
      <SalaryCenter
        calendarDays={calendarDays}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        onMonthChange={setSelectedMonth}
        onYearChange={setSelectedYear}
        selectedEmployee={profileView}
        timesheetSummary={timesheetSummary}
        timesheets={timesheets}
      />

      <ProfilePanel profile={profileView} />
    </MainGrid>
  );
}